import { jsPDF } from 'jspdf'
import { company, CONTACT_EMAIL } from '../data/site'
import { formatDate } from './format'
import { invoiceLineSubtotal, invoiceTotals } from './invoice'
import type { AdminSettings } from '../types/admin'
import type { Invoice } from '../types/invoice'

/** Filnavn: LYKKEVAERK-Faktura-FAK-2026-0007.pdf */
export function invoiceFileName(invoiceNumber: string): string {
  return `LYKKEVAERK-Faktura-${invoiceNumber || 'kladde'}.pdf`
}

const INK: [number, number, number] = [22, 35, 47]
const MUTED: [number, number, number] = [107, 122, 136]
const LINE: [number, number, number] = [222, 218, 210]
const ACCENT: [number, number, number] = [200, 150, 62]

const MARGIN = 18
const PAGE_W = 210
const PAGE_H = 297
const RIGHT = PAGE_W - MARGIN

function kr(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr.`
}

async function loadLogo(): Promise<string | null> {
  try {
    const response = await fetch(company.logoWide, { mode: 'cors' })
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * Bygger fakturaen som printvenlig A4-PDF i LYKKEVÆRKs identitet.
 * Bruger altid de senest gemte fakturadata. Den interne note kommer
 * aldrig med på kundens PDF.
 */
export async function buildInvoicePdf(invoice: Invoice, settings: AdminSettings): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  doc.setFont('helvetica', 'normal')

  const logo = await loadLogo()
  let y = MARGIN

  if (logo) {
    try {
      const props = doc.getImageProperties(logo)
      const height = 13
      const width = (props.width / props.height) * height
      doc.addImage(logo, 'PNG', MARGIN, y, width, height)
    } catch {
      doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
      doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
    }
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
    doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
  }

  /* Afsenderen står ét sted: kompakt øverst til højre. */
  const senderLines = [
    settings.companyName || 'LYKKEVÆRK',
    `CVR: ${settings.cvr}`,
    settings.phone,
    CONTACT_EMAIL,
  ].filter(Boolean)

  senderLines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal').setFontSize(index === 0 ? 9 : 8.5)
    doc.setTextColor(...(index === 0 ? INK : MUTED))
    doc.text(line, RIGHT, y + 3.5 + index * 4, { align: 'right' })
  })

  y += 32

  /* Titel og fakturanummer til venstre, datoer samlet til højre */
  doc.setFont('helvetica', 'bold').setFontSize(26).setTextColor(...INK)
  doc.text('Faktura', MARGIN, y)
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text(invoice.invoiceNumber, MARGIN, y + 6.5)

  const dateLabelX = RIGHT - 32
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED)
  doc.text('Fakturadato', dateLabelX, y - 1, { align: 'right' })
  doc.text('Forfaldsdato', dateLabelX, y + 5, { align: 'right' })
  doc.setFont('helvetica', 'bold').setTextColor(...INK)
  doc.text(formatDate(invoice.invoiceDate), RIGHT, y - 1, { align: 'right' })
  doc.text(formatDate(invoice.dueDate), RIGHT, y + 5, { align: 'right' })

  y += 18
  doc.setDrawColor(...LINE).setLineWidth(0.3)
  doc.line(MARGIN, y, RIGHT, y)
  y += 9

  /* Modtager — afsenderen gentages ikke her */
  doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
  doc.text('TIL', MARGIN, y)

  const postal = [invoice.customerPostalCode, invoice.customerCity].filter(Boolean).join(' ')
  const toLines = [
    invoice.customerAddress,
    postal,
    invoice.customerPhone,
    invoice.customerEmail,
  ].filter(Boolean)

  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...INK)
  doc.text(invoice.customerName || '—', MARGIN, y + 7)

  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  toLines.forEach((line, index) => {
    doc.text(line, MARGIN, y + 13.5 + index * 4.6)
  })

  y += 13.5 + toLines.length * 4.6 + 4
  doc.setDrawColor(...LINE)
  doc.line(MARGIN, y, RIGHT, y)
  y += 9

  /* Reference — betalingsbetingelserne står samlet i BETALING nederst */
  if (invoice.reference) {
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED)
    doc.text(`Reference: ${invoice.reference}`, MARGIN, y)
    y += 8
  }

  /* Beskrivelse */
  if (invoice.description) {
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
    const wrapped = doc.splitTextToSize(invoice.description, RIGHT - MARGIN)
    doc.text(wrapped, MARGIN, y)
    y += wrapped.length * 4.6 + 6
  }

  /* Tabel med fakturalinjer */
  const colQty = MARGIN + 80
  const colUnit = MARGIN + 100
  const colPrice = MARGIN + 126
  const colDiscount = MARGIN + 144
  const colTotal = RIGHT

  function tableHeader() {
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text('BESKRIVELSE', MARGIN, y)
    doc.text('ANTAL', colQty, y, { align: 'right' })
    doc.text('ENHED', colUnit, y, { align: 'right' })
    doc.text('PRIS', colPrice, y, { align: 'right' })
    doc.text('RABAT', colDiscount, y, { align: 'right' })
    doc.text('I ALT', colTotal, y, { align: 'right' })
    y += 2.5
    doc.setDrawColor(...LINE)
    doc.line(MARGIN, y, RIGHT, y)
    y += 5
  }

  tableHeader()
  doc.setFont('helvetica', 'normal').setFontSize(10)

  invoice.lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line.description || '—', 74)
    const rowHeight = Math.max(wrapped.length * 4.4, 6)

    if (y + rowHeight > PAGE_H - 34) {
      doc.addPage()
      y = MARGIN
      tableHeader()
      doc.setFont('helvetica', 'normal').setFontSize(10)
    }

    doc.setTextColor(...INK)
    doc.text(wrapped, MARGIN, y)
    doc.setTextColor(...MUTED)
    doc.text(String(line.quantity ?? 0), colQty, y, { align: 'right' })
    doc.text(line.unit, colUnit, y, { align: 'right' })
    doc.text(kr(line.unitPrice), colPrice, y, { align: 'right' })
    doc.text(line.discount ? `${line.discount} %` : '—', colDiscount, y, { align: 'right' })
    doc.setTextColor(...INK)
    doc.text(kr(invoiceLineSubtotal(line)), colTotal, y, { align: 'right' })

    y += rowHeight + 2
    doc.setDrawColor(240, 238, 233)
    doc.line(MARGIN, y - 1, RIGHT, y - 1)
    y += 2
  })

  if (invoice.lines.length === 0) {
    doc.setTextColor(...MUTED).setFontSize(10)
    doc.text('Ingen linjer på fakturaen.', MARGIN, y)
    y += 8
  }

  /* Totaler */
  const totals = invoiceTotals(invoice.lines)
  if (y + 34 > PAGE_H - 30) {
    doc.addPage()
    y = MARGIN
  }
  y += 4

  const labelX = RIGHT - 52
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text('Subtotal ekskl. moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.subtotal), RIGHT, y, { align: 'right' })

  y += 5.5
  doc.setTextColor(...MUTED)
  doc.text('Moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.vatAmount), RIGHT, y, { align: 'right' })

  y += 4
  doc.setDrawColor(...LINE)
  doc.line(labelX - 24, y, RIGHT, y)
  y += 6.5

  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...INK)
  doc.text('Total inkl. moms', labelX, y, { align: 'right' })
  doc.text(kr(totals.total), RIGHT, y, { align: 'right' })

  y += 14

  /* Betaling og bemærkninger — kun felter med indhold vises. */
  const paymentPairs = [
    ['Bank', invoice.bankName],
    ['Reg.nr.', invoice.registrationNumber],
    ['Kontonr.', invoice.accountNumber],
    ['Reference', invoice.paymentReference],
  ].filter(([, value]) => Boolean(value)) as [string, string][]

  const paymentMeta = [
    invoice.paymentTerms ? `Betalingsbetingelser: ${invoice.paymentTerms}` : '',
    invoice.dueDate ? `Forfaldsdato: ${formatDate(invoice.dueDate)}` : '',
  ].filter(Boolean)

  const paymentDetails = [
    ...paymentPairs.map(([label, value]) => `${label}: ${value}`),
    ...paymentMeta,
  ]

  // Betalingsteksten får en tom linje over sig, så oplysningerne står for sig selv.
  const paymentLines = [
    ...paymentDetails,
    ...(invoice.paymentText ? (paymentDetails.length > 0 ? ['', invoice.paymentText] : [invoice.paymentText]) : []),
  ]

  const blocks: { label: string; lines: string[] }[] = [
    { label: 'BETALING', lines: paymentLines },
    { label: 'BEMÆRKNINGER', lines: invoice.customerNote ? [invoice.customerNote] : [] },
  ].filter((block) => block.lines.length > 0)

  blocks.forEach((block) => {
    const wrapped = block.lines.flatMap((line) =>
      doc.splitTextToSize(line, RIGHT - MARGIN - 10),
    ) as string[]
    const boxHeight = wrapped.length * 4.6 + 14

    if (y + boxHeight > PAGE_H - 26) {
      doc.addPage()
      y = MARGIN
    }

    doc.setFillColor(248, 246, 242)
    doc.rect(MARGIN, y, RIGHT - MARGIN, boxHeight, 'F')
    doc.setFillColor(...ACCENT)
    doc.rect(MARGIN, y, 1.2, boxHeight, 'F')

    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text(block.label, MARGIN + 6, y + 6)
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...INK)
    doc.text(wrapped, MARGIN + 6, y + 11.5)

    y += boxHeight + 6
  })

  /* Sidefod */
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    doc.setDrawColor(...LINE).setLineWidth(0.3)
    doc.line(MARGIN, PAGE_H - 18, RIGHT, PAGE_H - 18)
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTED)
    doc.text(
      [
        settings.companyName || 'LYKKEVÆRK',
        `CVR: ${settings.cvr}`,
        settings.phone,
        CONTACT_EMAIL,
      ]
        .filter(Boolean)
        .join('  ·  '),
      MARGIN,
      PAGE_H - 12,
    )
    doc.text(`Side ${page} af ${pages}`, RIGHT, PAGE_H - 12, { align: 'right' })
  }

  return doc
}

export async function downloadInvoicePdf(
  invoice: Invoice,
  settings: AdminSettings,
): Promise<void> {
  const doc = await buildInvoicePdf(invoice, settings)
  doc.save(invoiceFileName(invoice.invoiceNumber))
}

/** Data-URL til forhåndsvisning i en indbygget PDF-visning. */
export async function invoicePdfDataUrl(
  invoice: Invoice,
  settings: AdminSettings,
): Promise<string> {
  const doc = await buildInvoicePdf(invoice, settings)
  return doc.output('datauristring')
}
